import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import ErrorBox from '../../core/components/ErrorBox';
import ResponsiveActionButton from '../../core/components/ResponsiveActionButton';
import { apiRawFetch } from '../../main/client';
import InviteUserDialog from './InviteUserDialog';

interface Invitation {
    id: number;
    email: string;
    name: string;
    status: string;
    expiresAt: string;
    invitedBy: { id: number; name: string } | null;
    createdAt: string;
    updatedAt: string;
}

const InvitationsTab = () => {
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchInvitations = async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch('api/invitations');

            if (!response.ok) {
                throw new Error('Failed to fetch invitations');
            }

            const data = await response.json();
            setInvitations(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitations();
    }, []);

    const handleCreateInvitation = () => {
        setDialogOpen(true);
    };

    const handleRevokeInvitation = async (invitationId: number) => {
        try {
            const response = await apiRawFetch(
                `api/invitations/${invitationId}/revoke`,
                'POST'
            );

            if (!response.ok) {
                throw new Error('Failed to revoke invitation');
            }

            await fetchInvitations();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to revoke invitation'
            );
        }
    };

    const handleResendInvitation = async (invitationId: number) => {
        try {
            const response = await apiRawFetch(
                `api/invitations/${invitationId}/resend`,
                'POST'
            );

            if (!response.ok) {
                throw new Error('Failed to resend invitation');
            }

            await fetchInvitations();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to resend invitation'
            );
        }
    };

    const handleInvitationSent = () => {
        setDialogOpen(false);
        fetchInvitations();
    };

    const isExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    const tableCellHeaderSx = {
        fontWeight: 700,
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        px: 2,
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    Pending Invitations
                </Typography>
                <ResponsiveActionButton
                    label="Invite User"
                    icon={<AddIcon />}
                    onClick={handleCreateInvitation}
                />
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '2px solid #374151',
                        borderRadius: 0,
                    }}
                >
                    <Table aria-label="invitations table">
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: '3px solid #6366f1',
                                    backgroundColor: '#1a1a2e',
                                }}
                            >
                                <TableCell sx={{ ...tableCellHeaderSx, py: 2 }}>
                                    Email
                                </TableCell>
                                <TableCell sx={tableCellHeaderSx}>Name</TableCell>
                                <TableCell sx={tableCellHeaderSx}>
                                    Invited By
                                </TableCell>
                                <TableCell sx={tableCellHeaderSx}>Expires</TableCell>
                                <TableCell sx={tableCellHeaderSx}>Status</TableCell>
                                <TableCell align="center" sx={tableCellHeaderSx}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invitations.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        align="center"
                                        sx={{ py: 6 }}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            No pending invitations
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                invitations.map(invitation => {
                                    const expired = isExpired(invitation.expiresAt);
                                    return (
                                        <TableRow
                                            key={invitation.id}
                                            sx={{
                                                borderBottom: '1px solid #374151',
                                                opacity: expired ? 0.5 : 1,
                                            }}
                                        >
                                            <TableCell sx={{ py: 2, px: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                >
                                                    {invitation.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 2 }}>
                                                <Typography variant="body2">
                                                    {invitation.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 2 }}>
                                                <Typography variant="body2">
                                                    {invitation.invitedBy?.name ??
                                                        'System'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ px: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {new Date(
                                                            invitation.expiresAt
                                                        ).toLocaleDateString()}
                                                    </Typography>
                                                    {expired && (
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    'inline-block',
                                                                px: 1.5,
                                                                py: 0.25,
                                                                border: '2px solid',
                                                                borderColor:
                                                                    'error.main',
                                                                backgroundColor:
                                                                    'rgba(239, 68, 68, 0.1)',
                                                                fontWeight: 700,
                                                                fontSize: '0.625rem',
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.05em',
                                                                color: 'error.main',
                                                            }}
                                                        >
                                                            Expired
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ px: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 2,
                                                        py: 0.5,
                                                        border: '2px solid',
                                                        borderColor:
                                                            invitation.status ===
                                                            'pending'
                                                                ? 'warning.main'
                                                                : '#374151',
                                                        backgroundColor:
                                                            invitation.status ===
                                                            'pending'
                                                                ? 'rgba(245, 158, 11, 0.1)'
                                                                : 'rgba(55, 65, 81, 0.1)',
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        color:
                                                            invitation.status ===
                                                            'pending'
                                                                ? 'warning.main'
                                                                : 'text.secondary',
                                                    }}
                                                >
                                                    {invitation.status}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ px: 2 }}>
                                                {!expired && (
                                                    <IconButton
                                                        onClick={() =>
                                                            handleResendInvitation(
                                                                invitation.id
                                                            )
                                                        }
                                                        size="small"
                                                        title="Resend invitation email"
                                                        sx={{
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(99, 102, 241, 0.1)',
                                                            },
                                                        }}
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    onClick={() =>
                                                        handleRevokeInvitation(
                                                            invitation.id
                                                        )
                                                    }
                                                    size="small"
                                                    title="Revoke invitation"
                                                    sx={{
                                                        color: 'error.main',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(239, 68, 68, 0.1)',
                                                        },
                                                    }}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <InviteUserDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSent={handleInvitationSent}
            />
        </Box>
    );
};

export default InvitationsTab;
